import { WEEKDAYS } from "@/types/doctor";
import type { DayTiming, OnboardingDraft } from "@/types/onboarding";

/**
 * FRONTEND-ONLY STORE.
 *
 * The documented backend (DOCTOR_REGISTRATION_API.md) has no endpoint for the
 * onboarding artefacts introduced by the profile-building flow (gender,
 * education, establishment, registration council, proofs, map coordinates,
 * timings, consultant details). Until the backend exposes them, the draft is
 * persisted in localStorage per Firebase UID. Backend-supported fields are
 * still sent through POST /api/doctors/register — nothing here replaces the
 * real API. See FRONTEND.MD → "Backend Integration Gaps".
 */
const KEY_PREFIX = "healnova.doctor.onboarding";

export function emptyTimings(): DayTiming[] {
  return WEEKDAYS.map((day) => ({
    day,
    enabled: day !== "Sunday",
    sessions: [{ from: "09:00", to: "13:00" }],
  }));
}

export function emptyDraft(): OnboardingDraft {
  return {
    fullName: "",
    gender: "",
    countryIso: "",
    phone: "",
    city: "",
    region: "",
    specialization: "",
    degree: "",
    institute: "",
    completionYear: "",
    experience: "",
    ownership: "",
    establishmentName: "",
    establishmentCity: "",
    locality: "",
    registrationNumber: "",
    registrationCouncil: "",
    registrationYear: "",
    fees: "",
    videoConsultationAvailable: false,
    bio: "",
    languages: [],
    identityProof: null,
    registrationProof: null,
    establishmentRole: "",
    establishmentProof: null,
    mapAddress: "",
    latitude: "",
    longitude: "",
    timings: emptyTimings(),
    consultant: null,
    completedSteps: [],
    updatedAt: new Date().toISOString(),
  };
}

const keyFor = (uid: string | null) => `${KEY_PREFIX}.${uid ?? "anonymous"}`;

export function loadDraft(uid: string | null): OnboardingDraft {
  if (typeof window === "undefined") return emptyDraft();
  try {
    const raw = window.localStorage.getItem(keyFor(uid));
    if (!raw) return emptyDraft();
    return { ...emptyDraft(), ...(JSON.parse(raw) as Partial<OnboardingDraft>) };
  } catch {
    return emptyDraft();
  }
}

export function saveDraft(uid: string | null, draft: OnboardingDraft): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      keyFor(uid),
      JSON.stringify({ ...draft, updatedAt: new Date().toISOString() }),
    );
  } catch {
    // Storage full or blocked — the wizard still works for this session.
  }
}

export function clearDraft(uid: string | null): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(keyFor(uid));
}
